import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import type { KeyboardLike, KeyWith } from '@/lib/piano/keyboard';
import type { TouchWeightAnalyzedKeyboard } from '@/lib/piano/touch-design/touch-weight-key-analysis';
import { benchAdjustment, isWorthAdjusting } from '@/lib/piano/touch-design/bench-adjustment';
import type { KeyAdjustmentPayload } from '../AdjustmentSheet.types';
export interface AdjustmentSummary {
  /** Keys whose front weight moves by at least the threshold. */
  frontWeightCount: number;
  frontWeightMean: number | null;
  hasSprings: boolean;
  /** Hammers whose strike weight moves by at least the threshold. */
  strikeWeightCount: number;
  strikeWeightMax: number | null;
  strikeWeightMin: number | null;
  strikeWeightUnchanged: number;
  springFirstKey: number | null;
  springLastKey: number | null;
  springCount: number;
}
export interface AdjustmentRows {
  keyboard: KeyboardLike<KeyWith<KeyAdjustmentPayload | null>>;
  summary: AdjustmentSummary;
}

/**
 * The sheet, and the three figures that summarise it.
 *
 * Both are derived here so that the tiles at the top of the page can never
 * disagree with the rows underneath them.
 */
export const useAdjustmentRows = (analyzedKeyboard, designedKeyboard) => {
  const $ = _c(34);
  let t0;
  if ($[0] !== analyzedKeyboard || $[1] !== designedKeyboard) {
    let t1;
    if ($[3] !== designedKeyboard) {
      t1 = analyzedKey => {
        const designedKey = designedKeyboard.getKeyByNumber(analyzedKey.number);
        const actualFrontWeight = analyzedKey.payload.frontWeight ?? null;
        const actualStrikeWeight = analyzedKey.payload.strikeWeight ?? null;
        const targetFrontWeight = designedKey?.payload.frontWeight ?? null;
        const targetStrikeWeight = designedKey?.payload.strikeWeight ?? null;
        if (actualFrontWeight === null || actualStrikeWeight === null || targetFrontWeight === null || targetStrikeWeight === null) {
          return null;
        }
        return {
          actualFrontWeight,
          actualStrikeWeight,
          actualSupportSpringBalanceWeight: analyzedKey.payload.supportSpringBalanceWeight ?? null,
          targetFrontWeight,
          targetStrikeWeight,
          targetSupportSpringBalanceWeight: designedKey?.payload.supportSpringBalanceWeight ?? null
        };
      };
      $[3] = designedKeyboard;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    t0 = analyzedKeyboard.map(t1);
    $[0] = analyzedKeyboard;
    $[1] = designedKeyboard;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const keyboard = t0;
  let rows;
  let strikeWeightDifferences;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[5] !== designedKeyboard || $[6] !== keyboard) {
    rows = keyboard.getKeys().filter(_temp);
    const frontWeightDifferences = rows.map(_temp2).filter(_temp3);
    strikeWeightDifferences = rows.map(_temp4).filter(_temp5);
    let t10;
    if ($[18] !== designedKeyboard) {
      t10 = designedKeyboard.getKeys().filter(_temp6);
      $[18] = designedKeyboard;
      $[19] = t10;
    } else {
      t10 = $[19];
    }
    const springKeys = t10;
    t1 = frontWeightDifferences.length;
    t2 = frontWeightDifferences.length === 0 ? null : frontWeightDifferences.reduce(_temp7, 0) / frontWeightDifferences.length;
    t3 = springKeys.length > 0;
    t4 = springKeys.length;
    t5 = springKeys[0]?.number ?? null;
    t6 = springKeys[springKeys.length - 1]?.number ?? null;
    t7 = strikeWeightDifferences.length;
    t8 = strikeWeightDifferences.length === 0 ? null : Math.max(...strikeWeightDifferences);
    t9 = strikeWeightDifferences.length === 0 ? null : Math.min(...strikeWeightDifferences);
    $[5] = designedKeyboard;
    $[6] = keyboard;
    $[7] = rows;
    $[8] = strikeWeightDifferences;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
  } else {
    rows = $[7];
    strikeWeightDifferences = $[8];
    t1 = $[9];
    t2 = $[10];
    t3 = $[11];
    t4 = $[12];
    t5 = $[13];
    t6 = $[14];
    t7 = $[15];
    t8 = $[16];
    t9 = $[17];
  }
  const t10 = rows.length - strikeWeightDifferences.length;
  let t11;
  if ($[20] !== t1 || $[21] !== t10 || $[22] !== t2 || $[23] !== t3 || $[24] !== t4 || $[25] !== t5 || $[26] !== t6 || $[27] !== t7 || $[28] !== t8 || $[29] !== t9) {
    t11 = {
      frontWeightCount: t1,
      frontWeightMean: t2,
      hasSprings: t3,
      springCount: t4,
      springFirstKey: t5,
      springLastKey: t6,
      strikeWeightCount: t7,
      strikeWeightMax: t8,
      strikeWeightMin: t9,
      strikeWeightUnchanged: t10
    };
    $[20] = t1;
    $[21] = t10;
    $[22] = t2;
    $[23] = t3;
    $[24] = t4;
    $[25] = t5;
    $[26] = t6;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  const summary = t11;
  let t12;
  if ($[31] !== keyboard || $[32] !== summary) {
    t12 = {
      keyboard,
      summary
    };
    $[31] = keyboard;
    $[32] = summary;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  return t12;
};
function _temp(row) {
  return row.payload !== null;
}
function _temp2(row_0) {
  return benchAdjustment(row_0.payload?.actualFrontWeight ?? null, row_0.payload?.targetFrontWeight ?? null);
}
function _temp3(change) {
  return isWorthAdjusting(change);
}
function _temp4(row_1) {
  return benchAdjustment(row_1.payload?.actualStrikeWeight ?? null, row_1.payload?.targetStrikeWeight ?? null);
}
function _temp5(change_0) {
  return isWorthAdjusting(change_0);
}
function _temp6(key) {
  return (key.payload.supportSpringBalanceWeight ?? null) !== null;
}
function _temp7(total, value) {
  return total + value;
}